/**
 * Seafoam Liquid Glass Design System — color tokens.
 *
 * Trimmed to just the `colors` token tree, the only export consumed in
 * this repo. Glass-effect / gradient / shadow / animation / data-viz /
 * utility-helper / button-style exports lifted from the deprecated app
 * surface had no consumers and were dropped during the post-lift orphan
 * cleanup. `@config/colors` retains the parallel set of helpers used by
 * pitch-deck and newsletter-overlay styling.
 */

  export const colors = {
    // Brand Colors - Seafoam Mist Family
    brand: {
      extraDark: '#99D5CA',    // Deep seafoam for strong accents
      primaryDark: '#B8E6DB',  // Rich seafoam for emphasis
      primary: '#D8F3ED',      // Main seafoam mist
      primaryLight: '#EBF9F6', // Soft seafoam for backgrounds
      extraLight: '#F5FCFB',   // Ultra-light seafoam tint
    },
  
    // Highlight Colors - Professional Slate Family
    highlight: {
      extraDark: '#1E293B',    // Deep slate for maximum contrast
      dark: '#334155',         // Dark slate for headings
      primary: '#475569',      // Main slate for CTAs
      light: '#64748B',        // Light slate for secondary elements
      extraLight: '#94A3B8',   // Soft slate for borders
      // Compatibility alias for legacy typo
      exxtraLight: '#94A3B8',
    },
  
    // Neutral Colors - Cool-tinted Greys
    neutral: {
      extraDark: '#1E293B',    // Darkest grey for text
      dark: '#334155',         // Dark grey for headings
      darkMedium: '#475569',   // Medium-dark for body text
      medium: '#64748B',       // Medium grey for secondary text
      mediumLight: '#94A3B8',  // Light-medium for muted text
      light: '#CBD5E1',        // Light grey for borders
      extraLight: '#E2E8F0',   // Extra light for backgrounds
      ultraLight: '#F1F5F9',   // Ultra light for subtle tints
      // Compatibility alias for legacy typo
      exxtraLight: '#E2E8F0',
    },
  
    // Accent Colors with Liquid Glass Variations
    accent: {
      pink: {
        extraDark: '#EC4899',
        dark: '#F9A8D4',
        base: '#FBCFE8',
        light: '#FCE7F3',
        extraLight: '#FDF2F8',
      },
      blue: {
        extraDark: '#0284C7',  // Rich ocean blue
        dark: '#38BDF8',       // Vibrant sky blue
        base: '#7DD3FC',       // Bright cyan blue
        light: '#BAE6FD',      // Soft sky blue
        extraLight: '#E0F2FE', // Ultra light blue
      },
      green: {
        extraDark: '#10B981',
        dark: '#86EFAC',
        base: '#BBF7D0',
        light: '#D9F99D',
        extraLight: '#ECFCCB',
      },
      yellow: {
        extraDark: '#F59E0B',
        dark: '#FCD34D',
        base: '#FDE68A',
        light: '#FEF3C7',
        extraLight: '#FFFBEB',
      },
      purple: {
        extraDark: '#6B5B95',  // Muted cool purple
        dark: '#9B90C2',       // Soft lavender purple
        base: '#C4B5FD',       // Light periwinkle
        light: '#DDD6FE',      // Very light lavender
        extraLight: '#F3F0FF', // Ultra light purple
      },
      coral: {
        extraDark: '#F87171',
        dark: '#FCA5A5',
        base: '#FBBCBC',
        light: '#FECACA',
        extraLight: '#FEE2E2',
      },
      red: {
        extraDark: '#B91C1C',
        dark: '#EF4444',
        base: '#F87171',
        light: '#FCA5A5',
        extraLight: '#FEE2E2',
      },
      magenta: {
        extraDark: '#A21CAF',
        dark: '#D946EF',
        base: '#E879F9',
        light: '#F0ABFC',
        extraLight: '#FAE8FF',
      },
      teal: {
        extraDark: '#14B8A6',
        dark: '#5EEAD4',
        base: '#99F6E4',
        light: '#CCFBF1',
        extraLight: '#F0FDFA',
      },
      orange: {
        extraDark: '#EA580C',
        dark: '#FB923C',
        base: '#FDBA74',
        light: '#FED7AA',
        extraLight: '#FFF7ED',
      },
      zen: {
        extraDark: '#124559',
        dark: '#19747E',
        base: '#1F9AA7',
        light: '#75D7DD',
        extraLight: '#D5F5F7',
      },
    },
  
    // Semantic Colors (gentle, mom-friendly)
    semantic: {
      success: '#86EFAC',       // accent.green.dark - gentle success
      warning: '#FCD34D',       // accent.yellow.dark - soft warning
      error: '#EF4444',         // accent.red.dark - clear error (not too harsh)
      info: '#7DD3FC',         // accent.blue.base - soft info
      successBg: '#ECFCCB',    // accent.green.extraLight
      warningBg: '#FEF3C7',    // accent.yellow.light
      errorBg: '#FEE2E2',      // accent.red.extraLight
      infoBg: '#E0F2FE',       // accent.blue.extraLight
    },
  
    // Legacy Compatibility & Quick Access
    text: '#1E293B',           // neutral.extraDark
    textSecondary: '#475569',  // neutral.darkMedium
    textMuted: '#64748B',      // neutral.medium
    textLight: '#94A3B8',      // neutral.mediumLight
    
    background: '#FFFFFF',     // Pure white
    surface: '#F5FCFB',       // brand.extraLight
    surfaceHover: '#EBF9F6',  // brand.primaryLight
    
    primary: '#D8F3ED',        // brand.primary
    primaryDark: '#B8E6DB',    // brand.primaryDark
    primaryLight: '#EBF9F6',   // brand.primaryLight
    
    // Borders with seafoam tint
    border: 'rgba(184, 230, 219, 0.2)',      // Soft seafoam border
    borderLight: 'rgba(184, 230, 219, 0.1)', // Very light border
    borderDark: '#B8E6DB',                   // Solid seafoam border
    
    // Liquid Glass Overlays
    overlayWhite10: 'rgba(255, 255, 255, 0.1)',
    overlayWhite20: 'rgba(255, 255, 255, 0.2)',
    overlayWhite30: 'rgba(255, 255, 255, 0.3)',
    overlayWhite35: 'rgba(255, 255, 255, 0.35)',
    overlayWhite40: 'rgba(255, 255, 255, 0.4)',
    overlayWhite45: 'rgba(255, 255, 255, 0.45)',
    overlayWhite50: 'rgba(255, 255, 255, 0.5)',
    overlayWhite55: 'rgba(255, 255, 255, 0.55)',
    overlayWhite60: 'rgba(255, 255, 255, 0.6)',
    overlayWhite65: 'rgba(255, 255, 255, 0.65)',
    overlayWhite70: 'rgba(255, 255, 255, 0.7)',
    overlayWhite80: 'rgba(255, 255, 255, 0.8)',
    overlayWhite90: 'rgba(255, 255, 255, 0.9)',
    
    // Dark overlays for modals
    darkOverlay10: 'rgba(30, 41, 59, 0.1)',
    darkOverlay20: 'rgba(30, 41, 59, 0.2)',
    darkOverlay30: 'rgba(30, 41, 59, 0.3)',
    darkOverlay40: 'rgba(30, 41, 59, 0.4)',
    darkOverlay45: 'rgba(30, 41, 59, 0.45)',
    darkOverlay50: 'rgba(30, 41, 59, 0.5)',
    darkOverlay60: 'rgba(30, 41, 59, 0.6)',
    
    // Seafoam overlays for brand elements
    seafoamOverlay10: 'rgba(216, 243, 237, 0.1)',
    seafoamOverlay20: 'rgba(216, 243, 237, 0.2)',
    seafoamOverlay30: 'rgba(216, 243, 237, 0.3)',
    seafoamOverlay40: 'rgba(216, 243, 237, 0.4)',
    seafoamOverlay50: 'rgba(216, 243, 237, 0.5)',
    
    // Social Colors
    google: '#4285F4',
    facebook: '#1877F2',
    linkedin: '#0A66C2',
    
    // Focus States
    focusRing: 'rgba(184, 230, 219, 0.5)',
    focusRingDark: 'rgba(71, 85, 105, 0.5)',
    
    // White variants
    white: '#FFFFFF',
    softWhite: '#FEFEFE',
    offWhite: '#FAFBFC',

    // Semantic aliases for legacy usage
    success: '#86EFAC',
    warning: '#FCD34D',
    error: '#EF4444',
    info: '#7DD3FC',

    // Compatibility aliases for legacy tokens
    // Glass surfaces (mapped to seafoam glass aesthetic)
    glassSurface: 'rgba(216, 243, 237, 0.2)',
    glassSurfaceHover: 'rgba(255, 255, 255, 0.3)',
    glassBorder: 'rgba(184, 230, 219, 0.25)',

    // Legacy white overlay degrees → mapped to overlayWhite variants
    overlayDegrees10: 'rgba(255, 255, 255, 0.1)',
    overlayDegrees15: 'rgba(255, 255, 255, 0.15)',
    overlayDegrees20: 'rgba(255, 255, 255, 0.2)',
    overlayDegrees25: 'rgba(255, 255, 255, 0.25)',
    overlayDegrees30: 'rgba(255, 255, 255, 0.3)',
    overlayDegrees35: 'rgba(255, 255, 255, 0.35)',
    overlayDegrees40: 'rgba(255, 255, 255, 0.4)',

    // Legacy brand overlays → mapped to seafoam overlays
    brandOverlay10: 'rgba(216, 243, 237, 0.1)',
    brandOverlay15: 'rgba(216, 243, 237, 0.15)',
    brandOverlay20: 'rgba(216, 243, 237, 0.2)',

    // Legacy black overlays → mapped to dark slate overlays
    blackOverlay20: 'rgba(30, 41, 59, 0.2)',
    blackOverlay30: 'rgba(30, 41, 59, 0.3)',
    blackOverlay40: 'rgba(30, 41, 59, 0.4)',

    // Legacy focus ring alias
    brandFocus: 'rgba(184, 230, 219, 0.5)',

    // Legacy shadow aliases
    shadowSoft: 'rgba(30, 41, 59, 0.12)',
    shadowHard: 'rgba(30, 41, 59, 0.22)',
  } as const;
